import React from 'react';
import { motion } from 'framer-motion';
import { LoaderIcon } from 'lucide-react';
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  children: React.ReactNode;
}
const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary-500 text-white
    hover:bg-primary-600 active:bg-primary-700
    focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
    disabled:bg-primary-300 disabled:cursor-not-allowed
  `,
  secondary: `
    bg-white text-primary-500 border-2 border-primary-500
    hover:bg-primary-50 active:bg-primary-100
    focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
    disabled:border-primary-200 disabled:text-primary-300 disabled:cursor-not-allowed
  `,
  ghost: `
    bg-transparent text-surface-700
    hover:bg-surface-100 active:bg-surface-200
    focus-visible:ring-2 focus-visible:ring-surface-400 focus-visible:ring-offset-2
    disabled:text-surface-400 disabled:cursor-not-allowed
  `,
  danger: `
    bg-error-500 text-white
    hover:bg-error-600 active:bg-error-700
    focus-visible:ring-2 focus-visible:ring-error-500 focus-visible:ring-offset-2
    disabled:bg-error-300 disabled:cursor-not-allowed
  `
};
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-body-sm gap-1.5',
  md: 'px-4 py-2.5 text-body-md gap-2',
  lg: 'px-6 py-3 text-body-lg gap-2.5'
};
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  disabled,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return <motion.button whileTap={{
    scale: isDisabled ? 1 : 0.98
  }} transition={{
    duration: 0.1
  }} disabled={isDisabled} className={`
        inline-flex items-center justify-center
        font-medium rounded-button
        transition-colors duration-150
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `} {...props} data-id="element-433">
      {loading ? <>
          <LoaderIcon className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-434" />
          <span data-id="element-435">Processing...</span>
        </> : <>
          {icon && iconPosition === 'left' && <span className="flex-shrink-0" aria-hidden="true" data-id="element-436">
              {icon}
            </span>}
          <span data-id="element-437">{children}</span>
          {icon && iconPosition === 'right' && <span className="flex-shrink-0" aria-hidden="true" data-id="element-438">
              {icon}
            </span>}
        </>}
    </motion.button>;
}